import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useServerOrderStats } from '@/hooks/useChartAnalytics'
import { useTranslation } from 'react-i18next'
import { Users } from 'lucide-react'
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts'
import { formatPrice } from '@/lib/utils'

interface ServerOrdersChartProps {
  restaurantId: string | null
  startDate: Date
  endDate: Date
}

// Colors for the pie chart
const COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#06b6d4', // cyan
  '#f97316', // orange
  '#ec4899', // pink
]

interface ChartDataItem {
  name: string
  value: number
  percentage: string
  revenue: number
  fill: string
}

export function ServerOrdersChart({ restaurantId, startDate, endDate }: ServerOrdersChartProps) {
  const { t } = useTranslation()
  const { data, isLoading } = useServerOrderStats(restaurantId, startDate, endDate)

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Users className="h-5 w-5" />
            {t('analytics.serverOrders')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <div className="w-32 h-32 rounded-full bg-muted animate-pulse" />
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Users className="h-5 w-5" />
            {t('analytics.serverOrders')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <p className="text-muted-foreground">{t('analytics.noServerData')}</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Calculate total orders for percentage
  const totalOrders = data.reduce((sum, item) => sum + item.orderCount, 0)

  // Transform data for recharts
  const chartData: ChartDataItem[] = data.map((item, index) => ({
    name: item.serverName,
    value: item.orderCount,
    percentage: totalOrders > 0 ? ((item.orderCount / totalOrders) * 100).toFixed(1) : '0',
    revenue: item.totalRevenue,
    fill: COLORS[index % COLORS.length]!,
  }))

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderCustomLabel = (props: any) => {
    return `${props.name} (${props.percentage}%)`
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload as ChartDataItem
      return (
        <div className="bg-background border rounded-lg shadow-lg p-3">
          <p className="font-medium">{data.name}</p>
          <p className="text-sm text-muted-foreground">
            {data.value} {t('analytics.orders')} ({data.percentage}%)
          </p>
          <p className="text-sm font-medium text-green-600">
            {formatPrice(data.revenue)} DH
          </p>
        </div>
      )
    }
    return null
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderLegend = (props: any) => {
    const { payload } = props
    return (
      <ul className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2 text-sm">
        {payload.map((entry: any, index: number) => {
          const item = chartData[index]
          return (
            <li key={`legend-${index}`} className="flex items-center gap-1">
              <span
                className="w-3 h-3 rounded-sm"
                style={{ backgroundColor: entry.color }}
              />
              <span>{entry.value}</span>
              <span className="text-muted-foreground">
                ({formatPrice(item?.revenue || 0)} DH)
              </span>
            </li>
          )
        })}
      </ul>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <Users className="h-5 w-5" />
          {t('analytics.serverOrders')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="45%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
                label={renderCustomLabel}
                labelLine={false}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend content={renderLegend} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  )
}
